import React, {useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {showHttpErrorToast} from '@common/http/show-http-error-toast';
import {Trans} from '@ui/i18n/trans';
import {Button} from '@ui/buttons/button';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {ConfirmationDialog} from '@ui/overlays/dialog/confirmation-dialog';
import {AccountSettingsPanel} from '@common/auth/ui/account-settings/account-settings-panel';
import {MovieIcon} from '@ui/icons/material/Movie';
import {SvgIconProps} from '@ui/icons/svg-icon';
import {User} from '@ui/types/user';

export const AUTHOR_ACCOUNT_PANEL_ID = 'author-account';

type RoleLike = {name: string};

function userRoles(user?: User | null): RoleLike[] {
  return ((user as any)?.roles ?? []) as RoleLike[];
}

function isAuthorUser(user?: User | null) {
  return userRoles(user).some(r => r.name === 'author');
}

function isAdminUser(user?: User | null) {
  return userRoles(user).some(r => r.name === 'admin' || r.name === 'admins');
}

// Reload the whole app so bootstrap data (roles/permissions) is refreshed.
function reloadTo(path: string) {
  window.location.assign(path);
}

function useBecomeAuthor() {
  return useMutation({
    mutationFn: () => apiClient.post('account/author').then(r => r.data),
    onSuccess: () => reloadTo('/admin/my-titles'),
    onError: err => showHttpErrorToast(err),
  });
}

function useRemoveAuthor() {
  return useMutation({
    mutationFn: () => apiClient.delete('account/author').then(r => r.data),
    onSuccess: () => reloadTo('/account-settings'),
    onError: err => showHttpErrorToast(err),
  });
}

/**
 * Painel nas definições da conta: ativar / remover conta autor.
 */
export function AuthorAccountPanel({user}: {user: User}) {
  const isAuthor = isAuthorUser(user);
  const isAdmin = isAdminUser(user);
  const removeAuthor = useRemoveAuthor();
  const becomeAuthor = useBecomeAuthor();
  const [removeOpen, setRemoveOpen] = useState(false);

  if (isAdmin) return null;

  return (
    <AccountSettingsPanel
      id={AUTHOR_ACCOUNT_PANEL_ID}
      title={<Trans message="Author account" />}
    >
      {isAuthor ? (
        <>
          <p className="mb-20 text-sm">
            <Trans message="Your account is an author account. You can publish and manage your works in the author panel." />
          </p>
          <div className="flex flex-wrap gap-12">
            <Button
              variant="flat"
              color="primary"
              onClick={() => reloadTo('/admin/my-titles')}
            >
              <Trans message="Go to author panel" />
            </Button>
            <DialogTrigger
              type="modal"
              isOpen={removeOpen}
              onOpenChange={setRemoveOpen}
              onClose={isConfirmed => {
                if (isConfirmed) removeAuthor.mutate();
              }}
            >
              <ConfirmationDialog
                isDanger
                title={<Trans message="Remove author account?" />}
                body={
                  <Trans message="Your account will go back to a regular account and you will lose access to the author panel. Your works already published are not deleted and you can become an author again later." />
                }
                confirm={<Trans message="Remove" />}
              />
            </DialogTrigger>
            <Button
              variant="outline"
              color="danger"
              disabled={removeAuthor.isPending}
              onClick={() => setRemoveOpen(true)}
            >
              <Trans message="Remove author account" />
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="mb-20 text-sm">
            <Trans message="Upgrade your account to an author account to publish your own works on Netkanema." />
          </p>
          <Button
            variant="flat"
            color="primary"
            disabled={becomeAuthor.isPending}
            onClick={() => becomeAuthor.mutate()}
          >
            <Trans message="Become an author" />
          </Button>
        </>
      )}
    </AccountSettingsPanel>
  );
}

// the sidenav renders panel icons with a custom viewBox; ignore it for material icons
export function AuthorAccountIcon(props: SvgIconProps) {
  const {viewBox, ...rest} = props as any;
  return <MovieIcon {...rest} />;
}
